/* Earn coins (Product information block) — keeps the coin figure in step with the selected variant
 * and the chosen quantity.
 *
 * The figure is a pure function of variant price × quantity, so it is recomputed client-side from
 * the variantChange payload and the quantity input instead of swapping server-rendered markup:
 * product-info.js only swaps its own fixed list of ids (price, Sku, Inventory, Volume,
 * Price-Per-Item). The maths here mirrors the Liquid in snippets/pi-earn-coins.liquid one-for-one,
 * so the first paint and every later paint agree.
 *
 * Rounding is applied to the line total, not per unit — see the snippet for why.
 *
 * Real balances, earning history and redemption belong to the rewards app — this only advertises
 * what buying this line earns.
 */
if (!customElements.get('pi-earn-coins')) {
  customElements.define(
    'pi-earn-coins',
    class PiEarnCoins extends HTMLElement {
      static COUNT_DURATION = 420;
      static BUMP_DURATION = 650;

      connectedCallback() {
        this.valueTarget = this.querySelector('[data-pi-coins-value]');
        this.labelTarget = this.querySelector('[data-pi-coins-label]');
        this.currentCoins = parseInt(this.valueTarget?.textContent, 10) || 0;
        this.priceCents = parseInt(this.dataset.priceCents, 10) || 0;
        this.quantity = this.readQuantity();

        // The quantity buttons dispatch a bubbling `change` on the input, and typing fires `input`,
        // so one delegated pair of listeners on the product-info host covers both. Listening on the
        // host rather than the input itself survives product-info.js rewriting the quantity form.
        this.onQuantityEvent = (event) => {
          if (!event.target.matches?.('.quantity__input')) return;
          if (event.target !== this.quantityInput) return;
          this.sync();
        };

        this.quantityHost = this.closest('product-info') || document;
        this.quantityHost.addEventListener('change', this.onQuantityEvent);
        this.quantityHost.addEventListener('input', this.onQuantityEvent);

        // Reconcile the first paint with the control's real value, silently: product-info.js is
        // cart-aware and can seat the quantity input below the minimum rule the snippet rendered with.
        this.render(false);

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          const variant = data && data.variant;
          if (!variant) return;

          // Other product forms on the page (featured product, quick add) publish on the same
          // channel, so only react to this section's own variant change.
          const sectionId = this.sectionId;
          if (sectionId && data.sectionId && String(data.sectionId) !== String(sectionId)) return;

          this.priceCents = parseInt(variant.price, 10) || 0;
          this.sync();
        });

        // product-info.js resets the quantity input to the new variant's minimum after a variant
        // change and announces it on this channel, so the figure has to follow that reset too.
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.sync());
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
        if (this.quantityHost && this.onQuantityEvent) {
          this.quantityHost.removeEventListener('change', this.onQuantityEvent);
          this.quantityHost.removeEventListener('input', this.onQuantityEvent);
        }
        if (this.countFrame) cancelAnimationFrame(this.countFrame);
        if (this.bumpTimer) clearTimeout(this.bumpTimer);
      }

      get sectionId() {
        const host = this.closest('product-info');
        if (!host) return null;
        return host.dataset.originalSection || host.dataset.section;
      }

      get quantityInput() {
        const host = this.closest('product-info');
        if (host) return host.querySelector('.quantity__input');

        const sectionId = this.id.replace('EarnCoins-', '');
        return document.getElementById(`Quantity-${sectionId}`);
      }

      get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      // A half-typed or cleared quantity box reads as 0/NaN; clamping to 1 keeps the figure from
      // flashing zero (and, with hide_when_zero, the whole panel from blinking out) mid-keystroke.
      readQuantity() {
        const input = this.quantityInput;
        const value = parseInt(input ? input.value : this.dataset.quantity, 10);
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      sync() {
        this.quantity = this.readQuantity();
        this.render();
      }

      coinsFor(priceCents, quantity) {
        const centsPerCoin = parseInt(this.dataset.centsPerCoin, 10);
        const coinsPerUnit = parseInt(this.dataset.coinsPerUnit, 10);
        const price = parseInt(priceCents, 10);
        const qty = parseInt(quantity, 10);

        if (!centsPerCoin || centsPerCoin <= 0 || !Number.isFinite(price) || !Number.isFinite(qty)) return 0;

        const raw = (price * qty) / centsPerCoin;
        let units;

        if (this.dataset.rounding === 'up') units = Math.ceil(raw);
        else if (this.dataset.rounding === 'nearest') units = Math.round(raw);
        else units = Math.floor(raw);

        return Math.max(0, units * (Number.isFinite(coinsPerUnit) ? coinsPerUnit : 1));
      }

      render(animate = true) {
        const coins = this.coinsFor(this.priceCents, this.quantity);
        const from = this.currentCoins;

        if (this.labelTarget) {
          const singular = this.dataset.labelSingular;
          this.labelTarget.textContent = coins === 1 && singular ? singular : this.dataset.label || '';
        }

        // Zero-earning lines (a $0 or sample variant) can be dropped entirely. The element still
        // renders server-side in that case — hidden — so a later variant can bring it back.
        if (this.dataset.hideWhenZero === 'true') {
          this.classList.toggle('hidden', coins === 0);
        }

        if (coins === from) return;

        this.currentCoins = coins;

        if (!animate || this.prefersReducedMotion) {
          this.paint(coins);
          return;
        }

        this.bump();
        this.countTo(from, coins);
      }

      paint(coins) {
        if (this.valueTarget) this.valueTarget.textContent = coins;
      }

      // The coin flip / ring pulse are CSS animations; this only carries the class that starts them,
      // and clears it so the next change can replay them.
      bump() {
        this.classList.remove('is-bumped');
        // Force a reflow, otherwise removing and re-adding the class in one frame is a no-op.
        void this.offsetWidth;
        this.classList.add('is-bumped');

        if (this.bumpTimer) clearTimeout(this.bumpTimer);
        this.bumpTimer = setTimeout(() => this.classList.remove('is-bumped'), PiEarnCoins.BUMP_DURATION);
      }

      countTo(from, to) {
        if (this.countFrame) cancelAnimationFrame(this.countFrame);

        const duration = PiEarnCoins.COUNT_DURATION;
        const start = performance.now();

        const step = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          // easeOutCubic — fast off the mark, settles on the final number
          const eased = 1 - Math.pow(1 - progress, 3);

          this.paint(Math.round(from + (to - from) * eased));

          if (progress < 1) {
            this.countFrame = requestAnimationFrame(step);
          } else {
            this.countFrame = null;
            this.paint(to);
          }
        };

        this.countFrame = requestAnimationFrame(step);
      }
    }
  );
}
